import { LOCAL_STORAGE_TWITTER_PROMPT_SEEN } from '@pkg/components/TwitterConnect/TwitterPromptModal';

import { apiFactory, Auth, Users } from '@app/client';
import { type SocialAuthorizationRequestBody } from '@app/client/common/structs/social/social';
import { TwitterConnectLocalStorage } from '@app/pages/GetStarted/TwitterConnect/types/enums';
import { registerEvent } from '@app/store/actions/events';
import { addNotification } from '@app/store/actions/notification';
import { initializeUser } from '@app/store/actions/user';

const errorMessages = {
    UNEXPECTED_ERROR: 'Unexpected error occurred',
    SOMETHING_WENT_WRONG: 'Something went wrong',
};

export enum TwitterConnectPageNames {
    undefined = 'undefined',
    twitterConnectModal = 'twitter-connect-modal',
    manageTwitterModal = 'manage-twitter-modal',
}

export async function redirectToTwitterAuthorizationPage(
    value: SocialAuthorizationRequestBody = {},
    page?: TwitterConnectPageNames,
): Promise<void> {
    const dispatch = apiFactory().dispatch;

    return apiFactory()
        .call(Auth.getTwitterAuthorizationUrl, { value })
        .then((response) => {
            if (response.ok) {
                registerEvent('twitter_connect', {
                    action: 'connect',
                    state: 'not-connected',
                    pageName: page || TwitterConnectPageNames.undefined,
                });

                // Remove the NY Twitter Connected Flag to avoid connection issues.
                localStorage.removeItem(TwitterConnectLocalStorage.UserNYTwitterConnected);
                window.location.href = response.value.path;
                return;
            }

            dispatch(
                addNotification({
                    message: response.error.detail || errorMessages.UNEXPECTED_ERROR,
                    title: response.error.title,
                    severity: 'error',
                }),
            );
        });
}

export async function getTwitterToken(oauth_token: string, oauth_verifier: string) {
    const dispatch = apiFactory().dispatch;
    // Do not make a request if Twitter is already connected.
    // Since we don't fail here, the requests goes straight to getting twitter data.
    if (localStorage.getItem(TwitterConnectLocalStorage.UserNYTwitterConnected)) {
        return;
    }

    return apiFactory()
        .call(Auth.twitterToken, { query: { oauth_token, oauth_verifier } })
        .then((response) => {
            if (response.ok) {
                localStorage.setItem(TwitterConnectLocalStorage.UserNYTwitterConnected, '1');
                localStorage.setItem(LOCAL_STORAGE_TWITTER_PROMPT_SEEN, '1');
                return;
            }
            dispatch(
                addNotification({
                    message: response.error.detail || errorMessages.SOMETHING_WENT_WRONG,
                    title: response.error.title,
                    severity: 'error',
                }),
            );
            return Promise.reject(false);
        });
}

export async function getUserTwitterData() {
    const dispatch = apiFactory().dispatch;

    return apiFactory()
        .call(Users.getUserTwitterData, {})
        .then((response) => {
            if (response.ok) {
                if (response.value?.value) {
                    const { screen_name } = response.value.value;
                    return `@${screen_name}`;
                }
                return;
            }
            dispatch(
                addNotification({
                    message: response.error.detail || errorMessages.SOMETHING_WENT_WRONG,
                    title: response.error.title,
                    severity: 'error',
                }),
            );
            return Promise.reject(false);
        });
}

export async function removeTwitterUserData() {
    const dispatch = apiFactory().dispatch;

    return apiFactory()
        .call(Users.removeUserTwitterData, {})
        .then((response) => {
            // if user deletes twitter account, we dont want them to go back to the onboarding flow, so effectively they skipped it.
            localStorage.setItem(TwitterConnectLocalStorage.UserSkippedTwitter, 'true');
            if (response.ok) {
                registerEvent('twitter_connect', {
                    action: 'remove',
                    state: 'not-connected',
                    pageName: TwitterConnectPageNames.manageTwitterModal,
                });

                // reinitialize user data (without twitter data)
                dispatch(initializeUser());

                return;
            }
            dispatch(
                addNotification({
                    message: response.error.detail || errorMessages.SOMETHING_WENT_WRONG,
                    title: response.error.title,
                    severity: 'error',
                }),
            );
            return Promise.reject(false);
        });
}
